import { Injectable } from '@angular/core';
import { Selector, State } from '@ngxs/store';
import _ from 'lodash';
import {
   IDynamicActionsBrickButtonsLayoutSettings,
   IDynamicActionsButtonsConfiguration as IDynamicActionsBrickButtonsConfiguration,
} from 'src/app/components/bricks/dynamic-actions-brick/dynamic-actions-brick-configuration/dynamic-actions-brick-buttons-configuration';
import { IDynamicActionsBrickConfiguration } from 'src/app/components/bricks/dynamic-actions-brick/dynamic-actions-brick-configuration/dynamic-actions-brick-configuration';
import { DynamicActionsBehaviorStateSelectors } from './dynamic-actions-behavior.selectors';
import { DynamicActionsBehaviorState, IDynamicActionsBehaviorStateModel } from './dynamic-actions-behavior.state';

@State<IDynamicActionsBehaviorStateModel>({
   name: 'dynamicActionsBehavior',
   defaults: {},
})
@Injectable()
export class DynamicActionsBehaviorStateButtonsSelectors extends DynamicActionsBehaviorStateSelectors {
   constructor() {
      super();
   }

   @Selector([DynamicActionsBehaviorState.getSpecificBehaviorStateOrDefault])
   public static getBrickButtonsConfiguration(getSpecificBehaviorStateOrDefaultFn: (brickInstanceId: string) => IDynamicActionsBrickConfiguration) {
      return (brickInstanceId: string) => {
         return getSpecificBehaviorStateOrDefaultFn(brickInstanceId) as IDynamicActionsBrickButtonsConfiguration;
      };
   }

   @Selector([DynamicActionsBehaviorStateButtonsSelectors.getBrickButtonsConfiguration])
   public static getSpecificButtons(getBrickButtonsConfiguration: (brickInstanceId: string) => IDynamicActionsBrickButtonsConfiguration) {
      return (brickInstanceId: string) => {
         return getBrickButtonsConfiguration(brickInstanceId)?.buttons ?? [];
      };
   }

   @Selector([DynamicActionsBehaviorStateButtonsSelectors.getBrickButtonsConfiguration])
   public static getBrickButtonSetting(getBrickButtonsConfiguration: (brickInstanceId: string) => IDynamicActionsBrickButtonsConfiguration) {
      return (brickInstanceId: string) => {
         return getBrickButtonsConfiguration(brickInstanceId)?.settings;
      };
   }

   @Selector([DynamicActionsBehaviorStateButtonsSelectors.getBrickButtonSetting])
   public static getSelectionMode(getBrickButtonsConfiguration: (brickInstanceId: string) => IDynamicActionsBrickButtonsLayoutSettings) {
      return (brickInstanceId: string) => {
         return getBrickButtonsConfiguration(brickInstanceId)?.selectionMode;
      };
   }

   @Selector([DynamicActionsBehaviorStateButtonsSelectors.getBrickButtonSetting])
   public static getOrientation(getBrickButtonsConfiguration: (brickInstanceId: string) => IDynamicActionsBrickButtonsLayoutSettings) {
      return (brickInstanceId: string) => {
         return getBrickButtonsConfiguration(brickInstanceId)?.orientation;
      };
   }

   @Selector([DynamicActionsBehaviorStateButtonsSelectors.getOrientation])
   public static getSpecificOrientation(getOrientation: (brickInstanceId: string) => 'vertical' | 'horizontal') {
      return (brickInstanceId: string): 'column' | 'row' => {
         const orientation = (getOrientation(brickInstanceId) ?? 'vertical').toLowerCase();
         return orientation === 'vertical' ? 'column' : 'row';
      };
   }

   @Selector([DynamicActionsBehaviorStateButtonsSelectors.getSelectionMode])
   public static getSelectionModeSanitized(getSelectionMode: (brickInstanceId: string) => 'single' | 'multiple' | 'none') {
      return (brickInstanceId: string) => {
         return (getSelectionMode(brickInstanceId) ?? 'none').toLowerCase();
      };
   }

   @Selector([DynamicActionsBehaviorStateButtonsSelectors.getSelectionModeSanitized])
   public static getSpecificSelectionMode(getSelectionMode: (brickInstanceId: string) => string) {
      return (brickInstanceId: string): 'single' | 'multiple' => {
         const selectionMode = getSelectionMode(brickInstanceId);
         if (_.includes(['single', 'multiple'], selectionMode)) {
            return selectionMode as 'single' | 'multiple';
         }
         // everything else (e.g. none), return single
         return 'single';
      };
   }

   @Selector([DynamicActionsBehaviorStateButtonsSelectors.getSelectionModeSanitized])
   public static getIsActionTogglable(getSelectionMode: (brickInstanceId: string) => 'single' | 'multiple' | 'none') {
      return (brickInstanceId: string) => {
         const selectionMode = getSelectionMode(brickInstanceId);
         // 'single' or 'multiple': true, 'none': false
         return selectionMode !== 'none';
      };
   }
}
